import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Image, Input, Layout, Menu, notification, PageHeader, Popconfirm, Row, Space, Table, Tooltip } from "antd";
import { useApplicationContext } from "components/Application";
import React, { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";

const collectionBrandsQuery = gql`
  query ($pagination: ConnectionPagination, $searchQuery: String) {
    collectionBrands(pagination: $pagination, searchQuery: $searchQuery) {
      nodes {
        id
        code
        position
        coverVertical
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const deleteCollectionBrandMutation = gql`
  mutation ($id: ID!) {
    deleteCollectionBrand(id: $id) {
      brand {
        id
        code
      }
    }
  }
`;

export const List: React.FC<{}> = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const navigate = useNavigate();
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "ASC", field: "ID" },
    searchQuery,
  };
  const { loading, data, refetch } = useQuery(collectionBrandsQuery, { variables, fetchPolicy: "cache-and-network" });

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const [execDelete] = useMutation(deleteCollectionBrandMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      notification.error({
        placement: "bottomRight",
        message: "Cannot delete this Brand",
      });
    },
    onCompleted: ({ deleteCollectionBrand: { brand } }) => {
      notification.success({
        placement: "bottomRight",
        message: `Brand "${brand.code}" deleted`,
      });
      refetch();
    },
  });

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
    },
    {
      title: "Position",
      dataIndex: "position",
    },
    {
      title: "Cover",
      dataIndex: "coverVertical",
      render: (value: string) => <Image src={`${appEndpointStore}${value}`} width={50} />,
    },
    {
      width: "80px",
      title: "Actions",
      dataIndex: "id",
      render: (id: string, resource: any) => (
        <Row justify="end">
          <Space>
            <Tooltip title="Edit" placement="bottom">
              <Button onClick={() => navigate(`${id}/edit`)} icon={<EditOutlined />}></Button>
            </Tooltip>
            <Popconfirm title="Are you sure?" icon={null} onConfirm={() => execDelete({ variables: { id } })}>
              <Tooltip title="Delete" placement="bottom">
                <Button danger icon={<DeleteOutlined />}></Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        </Row>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
            />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title="Brands"
          extra={
            <Button
              onClick={() => {
                navigate("new");
              }}
              type="primary"
            >
              <PlusOutlined />
              New
            </Button>
          }
        />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.collectionBrands.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              total: data?.totalCount ?? 0,
              defaultPageSize: 20,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
